/**
 * KPIIntegrityIndicator — persistent status chip for the dashboard-KPI invariants.
 *
 * PROTECTED KPI CONSUMER — reads `integrity_check` (the contract defined in
 * /app/memory/KPI_INTEGRITY.md) and renders:
 *   · a compact gold "Integrity ✓" pill when passing (clickable for detail)
 *   · an expanded red alert with the failing invariants and affected guests when failing
 *
 * Consumers: DashboardTab (always shown in header), MessagingTab and SendNoticesTab
 * (warning strip above send-critical actions), GuestListTab (top-of-list strip).
 */
import React, { useState, useMemo } from 'react';

function fmtTime(date) {
  if (!date) return '';
  try {
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

/**
 * Props
 *   integrityCheck: { passed: boolean, failures: string[], exceptions?: [...] }
 *   checkedAt: Date | string | null
 *   variant: 'header' | 'inline-warning'     (default 'header')
 *   context: string                          (e.g. 'messaging', 'notices') — shown in expanded detail
 *   onNavigate?: (tab, opts) => void         (used by the 'Resolve in Guests' button in header variant)
 */
export function KPIIntegrityIndicator({
  integrityCheck,
  checkedAt = null,
  variant = 'header',
  context = 'dashboard',
  onNavigate,
}) {
  const [expanded, setExpanded] = useState(false);
  const passed = integrityCheck?.passed !== false; // default to passing if unknown
  const failures = integrityCheck?.failures || [];
  const exceptions = integrityCheck?.exceptions || [];
  const timeStr = fmtTime(checkedAt);

  // When failing, force expanded in the header variant (but can still collapse/expand in inline-warning)
  const actuallyExpanded = !passed || expanded;

  const gold = '#C6A75A';
  const goldDark = '#8F7432';
  const red = '#B03030';
  const redBg = 'linear-gradient(90deg, #4A0E0E 0%, #6B1616 100%)';
  const cream = '#F4EFE3';

  // ── INLINE WARNING VARIANT ────────────────────────────────────────────────
  // Used inside critical workflow tabs. Only renders when integrity FAILS (stays silent when passing).
  if (variant === 'inline-warning') {
    if (passed) return null;
    return (
      <div
        data-testid={`kpi-integrity-warning-${context}`}
        role="alert"
        style={{
          margin: '0 0 14px',
          background: 'linear-gradient(90deg, #4A0E0E 0%, #5C1010 100%)',
          border: '1px solid rgba(255,120,120,0.4)',
          borderRadius: 8,
          padding: '10px 14px',
          color: '#FFD6D6',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          fontSize: 12,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          boxShadow: '0 2px 8px rgba(120,0,0,0.2)',
        }}
      >
        <div style={{ fontSize: 16, lineHeight: 1 }}>⚠</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 11, marginBottom: 2 }}>
            KPI integrity failing — numbers on this page may be unreliable
          </div>
          <div style={{ opacity: 0.9, fontSize: 11 }}>
            {failures[0] || 'Dashboard counters disagree.'} Review on the Dashboard tab before relying on these values for send or decision actions.
          </div>
        </div>
        {timeStr && (
          <div style={{ fontSize: 10, opacity: 0.7, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
            checked {timeStr}
          </div>
        )}
      </div>
    );
  }

  // ── HEADER VARIANT ────────────────────────────────────────────────────────
  return (
    <div
      data-testid="kpi-integrity-indicator"
      data-integrity-passed={passed}
      style={{
        position: 'relative',
        zIndex: 2,
        margin: '10px 0 4px',
      }}
    >
      {/* Compact always-visible pill */}
      <button
        type="button"
        data-testid="kpi-integrity-chip"
        aria-expanded={actuallyExpanded}
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '5px 12px 5px 10px',
          borderRadius: 999,
          border: passed ? `1px solid ${goldDark}` : `1px solid ${red}`,
          background: passed
            ? 'linear-gradient(180deg, rgba(198,167,90,0.18) 0%, rgba(198,167,90,0.08) 100%)'
            : redBg,
          color: passed ? goldDark : '#FFD6D6',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          cursor: 'pointer',
          boxShadow: passed ? 'none' : '0 1px 6px rgba(120,0,0,0.3)',
          transition: 'background 0.12s ease, transform 0.12s ease',
        }}
      >
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            width: 8, height: 8, borderRadius: '50%',
            background: passed ? gold : '#FF6A6A',
            boxShadow: passed ? `0 0 6px ${gold}` : '0 0 6px #FF6A6A',
          }}
        />
        <span>{passed ? 'KPI Integrity OK' : 'KPI Integrity Failing'}</span>
        {timeStr && (
          <span
            data-testid="kpi-integrity-checked-at"
            style={{ fontFamily: 'monospace', fontSize: 10, opacity: 0.75, fontWeight: 500, letterSpacing: 0 }}
          >
            · checked {timeStr}
          </span>
        )}
        <span aria-hidden style={{ fontSize: 9, opacity: 0.7 }}>{actuallyExpanded ? '▾' : '▸'}</span>
      </button>

      {/* Expanded detail panel */}
      {actuallyExpanded && (
        <div
          data-testid="kpi-integrity-detail"
          style={{
            marginTop: 10,
            background: passed ? cream : redBg,
            border: passed ? '1px solid rgba(198,167,90,0.35)' : '1px solid rgba(255,120,120,0.4)',
            borderRadius: 8,
            padding: '12px 14px',
            color: passed ? '#3A2E18' : '#FFD6D6',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            fontSize: 12,
            boxShadow: passed ? 'none' : '0 2px 10px rgba(120,0,0,0.25)',
          }}
        >
          {passed ? (
            <>
              <div style={{ fontWeight: 700, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>
                All invariants passing
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, lineHeight: 1.6 }}>
                <li>I1 total_invited == yes + no + pending</li>
                <li>I2a attending_total ≥ yes</li>
                <li>I2b unknown_age == 0 (every YES guest classified)</li>
                <li>I4 plate_cost base = adults + children only</li>
                <li>I7 all counters ≥ 0</li>
              </ul>
              <div style={{ opacity: 0.65, fontSize: 10, marginTop: 8, fontFamily: 'monospace' }}>
                Contract: /app/memory/KPI_INTEGRITY.md · Checked {timeStr || '—'}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>
                KPI Integrity Check Failed
              </div>
              <div style={{ fontSize: 12, opacity: 0.92, marginBottom: 8 }}>
                One or more dashboard counters disagree. Plate cost excludes affected guests until resolved.
                Workflows that rely on RSVP numbers (messaging, notices) may be operating on incomplete data.
              </div>
              <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 11, lineHeight: 1.5, fontFamily: 'monospace' }}>
                {failures.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
              {exceptions.map((ex, i) => (
                <div
                  key={i}
                  data-testid={`kpi-exception-${ex.code}`}
                  style={{
                    background: 'rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255,200,200,0.2)',
                    borderRadius: 6,
                    padding: '8px 10px',
                    marginTop: 6,
                    fontSize: 11,
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {ex.code === 'unknown_age_yes_guests'
                      ? `${ex.count} YES guest${ex.count === 1 ? '' : 's'} missing age classification`
                      : `${ex.code} (${ex.count})`}
                  </div>
                  {ex.guests && ex.guests.length > 0 && (
                    <div style={{ fontFamily: 'monospace', fontSize: 10, marginBottom: 6, opacity: 0.9 }}>
                      {ex.guests.slice(0, 10).map(g => g.name || g.guest_id).join(' · ')}
                    </div>
                  )}
                  <div style={{ opacity: 0.85, fontSize: 10 }}>{ex.remediation}</div>
                  {onNavigate && ex.code === 'unknown_age_yes_guests' && (
                    <button
                      type="button"
                      data-testid="kpi-exception-resolve-btn"
                      onClick={() => onNavigate('guests', { filter: 'yes', missingAgeGroup: true, exceptionGuestIds: (ex.guests || []).map(g => g.guest_id) })}
                      style={{
                        marginTop: 8,
                        padding: '5px 12px',
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        background: gold,
                        color: '#2A2010',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                      }}
                    >
                      Resolve in Guests →
                    </button>
                  )}
                </div>
              ))}
              <div style={{ opacity: 0.7, fontSize: 10, marginTop: 10, fontFamily: 'monospace' }}>
                Checked {timeStr || '—'} · Contract: /app/memory/KPI_INTEGRITY.md
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default KPIIntegrityIndicator;
